import { lazy } from "react"
import { Navigate } from "react-router-dom"
import { useAuthStore } from "@/stores/auth"
import { useGetUserQuery } from "@/queries/user"
import { AppBootstrapLoader } from "@/components/loaders/preloader"
import { LazyPage } from "@/router/lazy-page"
import { ADMIN_HOME } from "@/constants/paths/admin"
import {
  effectiveConsoleScope,
  getEligibleConsoleScopes,
  needsConsoleScopeChooser,
} from "@/helpers/console-scope"
import {
  CONSOLE_SCOPE_PLATFORM,
  SELECT_CONSOLE_SCOPE_PATH,
} from "@/constants/console-scope"
import type { AuthSession } from "@/types/auth"

const MarketingHome = lazy(() => import("@/pages/marketing"))

export function DashboardRedirect() {
  return <Navigate to="/dashboard" replace />
}

/**
 * On `/`, send authenticated users to their console destination.
 * Guests see the marketing homepage.
 */
export function RootRedirect() {
  const token = useAuthStore((state) => state.token)
  const consoleScope = useAuthStore((state) => state.consoleScope)
  const storeUser = useAuthStore((state) => state.user)
  const { data, isLoading, isError } = useGetUserQuery({}, { skip: !token })
  const isAuthenticated = !!token

  if (!isAuthenticated) {
    return null
  }

  const userData =
    (data as AuthSession | undefined) ??
    (storeUser as AuthSession | null | undefined) ??
    undefined

  // Wait for profile only when we have nothing usable yet.
  if (isLoading && !userData && !isError) {
    return (
      <div className="fixed inset-0 z-[100] bg-background">
        <AppBootstrapLoader />
      </div>
    )
  }

  const eligible = getEligibleConsoleScopes(userData ?? null)
  const effective = effectiveConsoleScope(consoleScope, userData ?? null)

  if (needsConsoleScopeChooser(eligible, effective)) {
    return <Navigate to={SELECT_CONSOLE_SCOPE_PATH} replace />
  }

  if (effective === CONSOLE_SCOPE_PLATFORM) {
    return <Navigate to={ADMIN_HOME} replace />
  }

  return <Navigate to="/dashboard" replace />
}

/** Root `/` page: redirect when authed, otherwise marketing home. */
export function RootHomePage() {
  const token = useAuthStore((state) => state.token)

  if (token) {
    return <RootRedirect />
  }

  return (
    <LazyPage fallback="bootstrap">
      <MarketingHome />
    </LazyPage>
  )
}
